<template>
  <div class="blueprint-generator">
    <div class="blueprint-canvas" :class="{ 'analyzing': isAnalyzing }">
      <svg viewBox="0 0 800 500" preserveAspectRatio="xMidYMid meet">
        <!-- Grid Background -->
        <defs>
          <pattern id="blueprint-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0,210,255,0.08)" stroke-width="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#blueprint-grid)" />

        <!-- Dynamic Content based on Heuristics -->
        <g v-if="!isAnalyzing">
          <!-- Physics Category: Force/Dynamics -->
          <g v-if="category === 'dynamics'">
            <rect x="350" y="200" width="100" height="100" fill="none" stroke="#00d2ff" stroke-width="2" />
            <line x1="450" y1="250" x2="600" y2="250" stroke="#ff4757" stroke-width="3" marker-end="url(#arrow-red)" />
            <text x="550" y="240" fill="#ff4757" font-size="14">F ({{ knowledgePoint }})</text>
            <line x1="400" y1="300" x2="400" y2="450" stroke="#ffd700" stroke-width="2" marker-end="url(#arrow-yellow)" />
            <text x="410" y="440" fill="#ffd700" font-size="14">G = mg</text>
          </g>

          <!-- Wave/Oscillation Category -->
          <g v-if="category === 'wave'">
            <path :d="wavePath" fill="none" stroke="#00d2ff" stroke-width="3" />
            <circle v-for="i in 5" :key="i" :cx="i * 150" :cy="250" r="4" fill="#00d2ff" opacity="0.5">
               <animate attributeName="r" values="4;8;4" dur="2s" :begin="i*0.2 + 's'" repeatCount="indefinite" />
            </circle>
            <text x="400" y="150" fill="#00d2ff" font-size="18" text-anchor="middle">{{ knowledgePoint }} 传播模型</text>
          </g>

          <!-- Geometry Category -->
          <g v-if="category === 'geometry'">
            <path :d="polyPath" fill="rgba(0,210,255,0.1)" stroke="#00d2ff" stroke-width="2" stroke-dasharray="8,4" />
            <text v-for="(p, i) in polyPoints" :key="i" :x="p.x + 10" :y="p.y + 10" fill="#888" font-size="12">V{{i+1}}</text>
            <text x="400" y="450" fill="#00d2ff" font-size="16" text-anchor="middle">几何构型分析: {{ knowledgePoint }}</text>
          </g>

          <!-- Default Logic/Concept -->
          <g v-if="category === 'general'">
            <circle cx="400" cy="250" r="150" fill="none" stroke="rgba(0,210,255,0.2)" stroke-dasharray="10,5" />
            <g v-for="i in 8" :key="i" :transform="`rotate(${i * 45}, 400, 250)`">
               <circle cx="400" cy="100" r="10" fill="#00d2ff">
                  <animate attributeName="opacity" values="0.2;1;0.2" dur="3s" :begin="i*0.4 + 's'" repeatCount="indefinite" />
               </circle>
               <line x1="400" y1="110" x2="400" y2="250" stroke="rgba(0,210,255,0.1)" />
            </g>
            <text x="400" y="260" fill="#fff" font-size="24" text-anchor="middle" font-weight="bold">{{ knowledgePoint }}</text>
          </g>
        </g>

        <!-- Common Markers -->
        <defs>
          <marker id="arrow-red" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#ff4757" />
          </marker>
          <marker id="arrow-yellow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L9,3 z" fill="#ffd700" />
          </marker>
        </defs>
      </svg>
    </div>

    <!-- AI Generation Overlay -->
    <transition name="fade">
      <div v-if="isAnalyzing" class="analysis-overlay">
        <div class="core-box">
          <div class="spinner"></div>
          <div class="msg">正在实时合成「{{ knowledgePoint }}」的科学演示模型...</div>
          <div class="sub">算法逻辑：{{ heuristicLog }}</div>
        </div>
      </div>
    </transition>

    <div class="gen-footer">
       <div class="info">
          <i class="el-icon-warning-outline"></i>
          <span>系统未找到预置精品组件，已启动「AI 启发式绘图引擎」生成即时模型。</span>
       </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScientificBlueprintViz',
  props: {
    knowledgePoint: { type: String, default: '未知探索' }
  },
  data() {
    return {
      isAnalyzing: true,
      category: 'general',
      heuristicLog: '无',
      time: 0,
      timer: null
    }
  },
  computed: {
    wavePath() {
      let d = "M 0 250";
      for (let x = 0; x <= 800; x += 10) {
        const y = 250 + Math.sin(x * 0.05 + this.time) * 40;
        d += ` L ${x} ${y}`;
      }
      return d;
    },
    polyPoints() {
      if (this.knowledgePoint.includes('三角')) return [{x:400,y:100}, {x:200,y:400}, {x:600,y:400}];
      return [{x:250,y:150}, {x:550,y:150}, {x:550,y:350}, {x:250,y:350}];
    },
    polyPath() {
      const pts = this.polyPoints;
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x} ${pts[i].y}`;
      return d + " Z";
    }
  },
  mounted() {
    this.runHeuristics();
    this.animate();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  },
  methods: {
    runHeuristics() {
      const kp = this.knowledgePoint;
      let target = 'general';
      let log = '匹配到通用概念，使用逻辑拓扑渲染';

      if (kp.includes('力') || kp.includes('运动') || kp.includes('平抛')) {
        target = 'dynamics';
        log = '检测到动力学关键词，合成力矢量模型';
      } else if (kp.includes('波') || kp.includes('光') || kp.includes('场') || kp.includes('效应')) {
        target = 'wave';
        log = '检测到波动/连续场特征，渲染函数映射';
      } else if (kp.includes('形') || kp.includes('定理') || kp.includes('等腰')) {
        target = 'geometry';
        log = '检测到几何特征，执行参数化绘图';
      }

      this.heuristicLog = log;
      setTimeout(() => {
        this.category = target;
        this.isAnalyzing = false;
      }, 1500);
    },
    animate() {
      this.time += 0.05;
      this.timer = requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.blueprint-generator {
  flex: 1;
  background: #02040a;
  display: flex;
  flex-direction: column;
  position: relative;
}

.blueprint-canvas {
  flex: 1;
  position: relative;
  transition: filter 0.5s;
  &.analyzing { filter: blur(5px) grayscale(0.5); }
}

.analysis-overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(2, 4, 10, 0.6);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;

  .core-box {
    text-align: center;
    .spinner {
      width: 40px; height: 40px;
      border: 3px solid rgba(0, 210, 255, 0.1);
      border-top-color: #00d2ff;
      border-radius: 50%;
      margin: 0 auto 20px;
      animation: spin 1s infinite linear;
    }
    .msg { color: #fff; font-size: 1.1rem; margin-bottom: 10px; }
    .sub { color: #555; font-size: 0.8rem; font-family: monospace; }
  }
}

.gen-footer {
  padding: 15px 30px;
  background: rgba(0, 210, 255, 0.03);
  border-top: 1px solid rgba(0, 210, 255, 0.1);
  .info {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #ffd700;
    font-size: 0.85rem;
    opacity: 0.8;
  }
}

@keyframes spin { to { transform: rotate(360deg); } }
.fade-enter-active, .fade-leave-active { transition: opacity 0.5s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
